import React from 'react';

interface ResidentSession {
  residentId: number;
  residentName: string;
  residentPhone: string;
  hourmeterId: number; // Horometro donde se verificó inicialmente
  verifiedAt: number; // timestamp
  expiresAt: number; // timestamp
}

const SESSION_KEY = 'resident_session';
const SESSION_DURATION = 15 * 60 * 1000; // 15 minutos en milisegundos

export class ResidentSessionManager {
  /**
   * Crear una nueva sesión de residente
   */
  static createSession(
    residentId: number,
    residentName: string,
    residentPhone: string,
    hourmeterId: number
  ): void {
    const now = Date.now();
    const session: ResidentSession = {
      residentId,
      residentName,
      residentPhone,
      hourmeterId,
      verifiedAt: now,
      expiresAt: now + SESSION_DURATION,
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  /**
   * Obtener la sesión activa (si existe y no ha expirado)
   */
  static getActiveSession(): ResidentSession | null {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      
      if (!sessionData) {
        return null;
      }

      const session: ResidentSession = JSON.parse(sessionData);
      const now = Date.now();

      // Verificar si la sesión ha expirado
      if (now > session.expiresAt) {
        this.clearSession();
        return null;
      }

      return session;
    } catch (error) {
      console.error('Error reading resident session:', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Verificar si hay una sesión activa para un residente específico
   */
  static isSessionActive(residentId?: number): boolean {
    const session = this.getActiveSession();
    if (!session) return false;
    
    // Si se especifica residentId, verificar que coincida
    if (residentId && session.residentId !== residentId) return false;
    
    return true;
  }

  /**
   * Obtener el tiempo restante de la sesión en milisegundos
   */
  static getTimeRemaining(): number {
    const session = this.getActiveSession();
    if (!session) return 0;
    
    const now = Date.now();
    const remaining = session.expiresAt - now;
    
    return Math.max(0, remaining);
  }

  /**
   * Formatear el tiempo restante como string legible
   */
  static getTimeRemainingFormatted(): string {
    const remaining = this.getTimeRemaining();
    if (remaining <= 0) return '0:00';
    
    const minutes = Math.floor(remaining / (60 * 1000));
    const seconds = Math.floor((remaining % (60 * 1000)) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Extender la sesión (reiniciar el timer)
   */
  static extendSession(): boolean {
    const session = this.getActiveSession();
    if (!session) return false;

    const now = Date.now();
    session.expiresAt = now + SESSION_DURATION;
    
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return true;
  }

  /**
   * Limpiar la sesión
   */
  static clearSession(): void {
    localStorage.removeItem(SESSION_KEY);
  }

  /**
   * Obtener información resumida de la sesión para mostrar en UI
   */
  static getSessionInfo(): { name: string; timeRemaining: string } | null {
    const session = this.getActiveSession();
    if (!session) return null;

    return {
      name: session.residentName,
      timeRemaining: this.getTimeRemainingFormatted(),
    };
  }
}

// Hook personalizado para React
export const useResidentSession = () => {
  const [sessionInfo, setSessionInfo] = React.useState<{
    isActive: boolean;
    name?: string;
    timeRemaining?: string;
  }>({ isActive: false });

  React.useEffect(() => {
    const updateSessionInfo = () => {
      const info = ResidentSessionManager.getSessionInfo();
      setSessionInfo({
        isActive: !!info,
        name: info?.name,
        timeRemaining: info?.timeRemaining,
      });
    };

    // Actualizar inmediatamente
    updateSessionInfo();

    // Actualizar cada segundo
    const interval = setInterval(updateSessionInfo, 1000);

    return () => clearInterval(interval);
  }, []);

  const clearSession = () => {
    ResidentSessionManager.clearSession();
    setSessionInfo({ isActive: false });
  };

  const extendSession = () => {
    ResidentSessionManager.extendSession();
  };

  return {
    ...sessionInfo,
    clearSession,
    extendSession,
  };
};

export default ResidentSessionManager;
